import React from 'react'

import { Redirect, useHistory, useRouteMatch } from 'react-router-dom'

import { RouteParams, routes } from 'constants/routes'
import NotFoundView from 'views/NotFoundView'
import LoadingState from 'components/LoadingState'
import AcceptMembershipPanel from './AcceptMembershipPanel'
import {
  ShareViewerHasUnacceptedMembershipInviteFragmentDoc,
  useGetShareMembershipByShareShortIdQuery,
} from 'gqlTypes'
import { Flex } from 'components/Box'
import useAcceptShareMembershipInvitation from '../operations/useAcceptShareMembershipInvitation'
import { useToast } from 'containers/ToastManager'
import useUserProfile from 'modules/user/useUserProfile'
import IndexLayout from 'components/IndexLayout'

const AcceptShareInviteByShareShortIdView: React.FC<{}> = () => {
  const match = useRouteMatch<RouteParams<'SHARE_INVITE'>>()
  const history = useHistory()
  const { showToast } = useToast()

  const shareIdentifier = match?.params?.shareID
  const { data: profile } = useUserProfile()

  const { data, error, loading } = useGetShareMembershipByShareShortIdQuery({
    variables: { shareIdentifier },
  })

  const [
    acceptShareMembership,
    {
      loading: acceptShareMembershipLoading,
      error: acceptShareMembershipError,
    },
  ] = useAcceptShareMembershipInvitation({
    variables: { shareIdentifier },
    update: cache => {
      cache.writeFragment({
        id: `Share::${shareIdentifier}`,
        fragment: ShareViewerHasUnacceptedMembershipInviteFragmentDoc,
        data: {
          userHasUnacceptedInvitationToView: false,
          __typename: 'Share',
        },
      })
    },
    onCompleted: () => {
      showToast('Invite successfully accepted')
      history.push(routes.SHARE_VIEW.create({ shareID: shareIdentifier }))
    },
    onError: 'do-nothing', // we use acceptShareMembershipError to show the error message
  })

  if (loading) {
    return <LoadingState />
  }

  if (error || !data?.shareMembership || !data?.share) {
    return <NotFoundView />
  }

  if (data.shareMembership.inviteStatus === 'ACCEPTED') {
    return (
      <Redirect
        to={routes.SHARE_VIEW.create({
          shareID: shareIdentifier,
        })}
      />
    )
  }

  const documentName = data?.share?.name ?? ''
  const inviter = data?.shareMembership?.inviter || undefined
  const userEmail = profile?.me.email ?? ''
  const userAvatar = profile?.me.avatar?.small ?? ''

  return (
    <IndexLayout variant="teal">
      <Flex maxWidth={470} width="100%" mt={14} alignSelf="center">
        <AcceptMembershipPanel
          inviter={inviter}
          documentName={documentName}
          acceptShareMembership={() =>
            acceptShareMembership({
              variables: { shareIdentifier },
            })
          }
          acceptShareMembershipLoading={acceptShareMembershipLoading}
          acceptShareMembershipError={acceptShareMembershipError}
          userInfo={{
            userEmail,
            userAvatar,
          }}
        />
      </Flex>
    </IndexLayout>
  )
}

export default AcceptShareInviteByShareShortIdView
